import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Stack,
  Plus,
  Trash,
  PencilSimple,
  Barbell,
  CaretRight,
  CaretDown,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDateIT } from "@/lib/formatDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", description: "" };

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/workout-templates`);
      setTemplates(data || []);
    } catch {
      toast.error("Errore nel caricamento");
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowDialog(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({ title: tpl.title || "", description: tpl.description || "" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.title.trim()) {
      toast.error("Inserisci un titolo");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/workout-templates/${editing.id}`, {
          title: form.title.trim(),
          description: form.description.trim(),
        });
        toast.success("Template aggiornato");
      } else {
        await axios.post(`${API}/workout-templates`, {
          title: form.title.trim(),
          description: form.description.trim(),
          exercises: [],
        });
        toast.success("Template creato");
      }
      setShowDialog(false);
      setEditing(null);
      setForm(EMPTY);
      fetchTemplates();
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo template? Non influisce sulle schede già instanziate.")) return;
    try {
      await axios.delete(`${API}/workout-templates/${id}`);
      toast.success("Template eliminato");
      fetchTemplates();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleExpanded = (id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  };

  return (
    <div data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            TEMPLATE
          </h1>
          <p className="text-zinc-500 text-sm mt-1 tracking-wide">
            Schede riutilizzabili da assegnare ai clienti con un click
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
          data-testid="new-template-button"
        >
          <Plus size={18} className="mr-2" /> Nuovo Template
        </Button>
      </div>

      {templates.length === 0 ? (
        <div className="text-center py-16 bg-[#171717] border border-[#262626] rounded-lg">
          <Stack size={40} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 font-medium">Nessun template salvato</p>
          <p className="text-zinc-600 text-sm mt-1 max-w-md mx-auto">
            Crea un template oppure salva una scheda esistente come template dal menu di una scheda nel dettaglio cliente
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((tpl, i) => {
            const isOpen = !!expanded[tpl.id];
            const exCount = tpl.exercises?.length || 0;
            return (
              <motion.div
                key={tpl.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="bg-[#0F0F0F] border border-[#262626] rounded-xl overflow-hidden"
                data-testid={`template-card-${tpl.id}`}
              >
                <div
                  className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                  onClick={() => toggleExpanded(tpl.id)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4FF00]/20 to-[#D4FF00]/5 border border-[#D4FF00]/20 flex items-center justify-center flex-shrink-0">
                      <Stack size={20} weight="duotone" className="text-[#D4FF00]" />
                    </div>
                    <div className="min-w-0">
                      <h3
                        className="font-bold text-base tracking-tight uppercase text-white truncate"
                        style={{ fontFamily: "Barlow Condensed, sans-serif" }}
                      >
                        {tpl.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-0.5 text-[11px] text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Barbell size={11} weight="fill" />
                          {exCount} eserciz{exCount === 1 ? "io" : "i"}
                        </span>
                        {tpl.updated_at && (
                          <span>Agg. {formatDateIT(tpl.updated_at.slice(0, 10))}</span>
                        )}
                        {tpl.description && (
                          <span className="truncate hidden sm:inline">— {tpl.description}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(tpl);
                      }}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-600 hover:text-[#007AFF] hover:bg-[#007AFF]/10 transition-all"
                      data-testid={`edit-template-${tpl.id}`}
                    >
                      <PencilSimple size={15} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(tpl.id);
                      }}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
                      data-testid={`delete-template-${tpl.id}`}
                    >
                      <Trash size={15} />
                    </button>
                    {isOpen ? <CaretDown size={16} className="text-zinc-500" /> : <CaretRight size={16} className="text-zinc-500" />}
                  </div>
                </div>
                {isOpen && (
                  <div className="px-5 pb-4 border-t border-[#1A1A1A]">
                    {exCount === 0 ? (
                      <p className="text-zinc-600 text-sm py-3">
                        Nessun esercizio nel template. Per modificare la lista, salva una scheda esistente come template dalla pagina di un cliente.
                      </p>
                    ) : (
                      <div className="mt-3 space-y-1.5">
                        {tpl.exercises.map((ex, idx) => (
                          <div
                            key={`${tpl.id}-${ex.name || "ex"}-${idx}`}
                            className="flex items-center justify-between bg-[#0A0A0A] border border-[#1A1A1A] rounded-md px-3 py-2"
                          >
                            <span className="text-sm text-white">{ex.name || "—"}</span>
                            <span className="text-[11px] text-zinc-500">
                              {ex.sets > 0 ? `${ex.sets} × ${ex.reps || "?"}` : "—"}
                              {ex.rest_seconds > 0 && ` · ${ex.rest_seconds}s recupero`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent
          className="bg-[#171717] border-[#262626] text-white max-w-md"
          data-testid="template-dialog"
        >
          <DialogHeader>
            <DialogTitle
              className="font-bold tracking-tight uppercase"
              style={{ fontFamily: "Barlow Condensed, sans-serif" }}
            >
              {editing ? "MODIFICA TEMPLATE" : "NUOVO TEMPLATE"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Titolo</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 bg-[#0A0A0A] border-[#262626] text-white"
                placeholder="es. Upper Body Standard"
                data-testid="template-title-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Descrizione</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="mt-1 bg-[#0A0A0A] border-[#262626] text-white min-h-[60px] resize-none"
                placeholder="Note utili per ricordare quando usarlo"
                data-testid="template-description-input"
              />
            </div>
            {!editing && (
              <p className="text-[11px] text-zinc-600">
                Il template viene creato vuoto. Per popolare gli esercizi, salva una scheda
                esistente come template dal menu di una scheda nel dettaglio cliente.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="border-[#262626] text-zinc-300 hover:bg-[#1A1A1A]"
              data-testid="template-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
              data-testid="template-save"
            >
              {editing ? "Aggiorna" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
